import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  inventoryControllerListMaterials,
  suppliersControllerCreate,
  suppliersControllerList,
  suppliersControllerRemove,
  suppliersControllerUpdate,
} from '@dvvb/sdk';

import {
  AlertBanner,
  Badge,
  Button,
  EmptyState,
  FormField,
  Icon,
  Modal,
  Select,
  TextInput,
  Textarea,
  Toggle,
  useToast,
} from '../../components/primitives';
import { ApiCallError, unwrap } from '../../lib/envelope';
import { useAuth } from '../../auth/AuthContext';
import { API_BASE, getStoredToken } from '../../lib/api-client';
import { queryClient } from '../../lib/queryClient';
import { useViewport } from '../../lib/useViewport';
import type { InventoryMaterialRow } from '../../types/api';
import '../inventory/supplier-auto-design.css';

interface SupplierRow {
  id: string;
  companyId: string;
  name: string;
  phone: string | null;
  address: string | null;
  contactName: string | null;
  monthlyVolumeWon: string | null;
  autoOrderEnabled: boolean;
  defaultLeadTimeDays?: number;
  smsRecipient?: string | null;
  boxheroPartnerId: string | null;
  status: 'active' | 'inactive';
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface SuppliersEnvelope {
  items: SupplierRow[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

interface MaterialsEnvelope {
  items: InventoryMaterialRow[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

type OnlineOrderSessionAuthStatus =
  | 'unknown'
  | 'login_required'
  | 'authenticated'
  | 'captcha_required'
  | 'blocked';

interface OnlineOrderRunnerSession {
  sessionId: string;
  platform: 'coupang';
  supplierId: string;
  supplierName?: string;
  loginUrl?: string;
  authStatus?: OnlineOrderSessionAuthStatus;
  lastOpenedUrl?: string;
  lastLoginStartAt?: string;
  lastStatusCheckAt?: string;
  browserStatus?: 'closed' | 'open';
  browserUrl?: string;
  lastBrowserOpenAt?: string;
  lastBrowserCloseAt?: string;
  blockers?: string[];
}

interface OnlineOrderRunnerBrowserControl {
  status?: string;
  url?: string;
  openedUrl?: string;
  openedAt?: string;
  blockers?: string[];
}

interface OnlineOrderRunnerSessionResponse {
  item: OnlineOrderRunnerSession;
  browser?: OnlineOrderRunnerBrowserControl;
  request?: {
    loginUrl?: string;
    sessionId?: string;
  };
  boundaries?: string[];
}

interface SupplierAutomationSettings {
  orderChannel: string;
  paymentMode: string;
  approvalMode: string;
  fallbackMode: string;
}

interface SupplierPlatformProfile {
  executionTarget: string;
  mobileOpenBehavior: string;
  automationScope: string;
  authChallengeHandling: string;
  searchableProducts: boolean;
  orderHistoryAvailable: boolean;
  directUrlOpen: boolean;
  optionSelectable: boolean;
  alwaysLoggedIn: boolean;
  smsVerificationRequired: boolean;
  emailVerificationRequired: boolean;
  googleVerificationRequired: boolean;
  otherVerificationRequired: boolean;
  paymentPasswordRequired: boolean;
  cardRegistered: boolean;
}

interface SupplierDesignSettings extends SupplierAutomationSettings, SupplierPlatformProfile {
  channelType: string;
  channel: string;
  channelAccount: string;
  channelTarget: string;
  orderMethod: string;
  connectorMode: string;
  defaultOrderTime: string;
}

interface SupplyItem {
  internalName: string;
  supplierName: string;
  unit: string;
  defaultQty: number;
  safetyStock: number;
  autoEnabled: '사용' | '미사용';
  memo: string;
  sourceKind: 'inventory' | null;
  sourceId: string | null;
  sourceName: string;
  sourceUnit: string;
  sourceLocationId: string | null;
  sourceLocationName: string | null;
  providerKind: InventoryMaterialRow['providerKind'];
  providerItemId: string | null;
  orderUnit: string;
  conversionRatio: number | null;
}

const CHANNEL_TYPE_OPTIONS = [
  { value: 'online', label: '온라인 플랫폼형' },
  { value: 'offline', label: '오프라인 발주수단형' },
];

const ONLINE_CHANNEL_OPTIONS = [
  { value: 'personal_mall', label: '개인몰' },
  { value: 'naver', label: '네이버' },
  { value: 'coupang', label: '쿠팡' },
  { value: 'other_platform', label: '기타 플랫폼' },
];

const OFFLINE_CHANNEL_OPTIONS = [
  { value: 'sms', label: '문자' },
  { value: 'kakao', label: '카카오톡' },
  { value: 'phone', label: '전화' },
  { value: 'email', label: '이메일' },
];

const ORDER_CHANNEL_OPTIONS = [
  { value: 'sms', label: 'SMS 발주' },
  { value: 'phone', label: '전화 확인' },
  { value: 'manual', label: '수기 처리' },
];

const PAYMENT_MODE_OPTIONS = [
  { value: 'none', label: '결제 없음' },
  { value: 'manual', label: '수동 결제 확인' },
  { value: 'mcp_pending', label: '온라인 결제 MCP 예정' },
];

const EXECUTION_TARGET_OPTIONS = [
  { value: 'user_mobile', label: '사용자 모바일' },
  { value: 'desktop_web', label: 'PC 웹' },
  { value: 'server_browser', label: '서버 격리 브라우저' },
  { value: 'mixed', label: '혼합' },
];

const MOBILE_OPEN_BEHAVIOR_OPTIONS = [
  { value: 'unknown', label: '미확인' },
  { value: 'web_stays', label: '모바일 웹 유지' },
  { value: 'app_link', label: '앱으로 열림' },
  { value: 'blocked', label: '모바일 사용 불가' },
];

const AUTOMATION_SCOPE_OPTIONS = [
  { value: 'handoff_only', label: '링크 열기/사용자 완료' },
  { value: 'search_ready', label: '검색/상품 확인' },
  { value: 'cart_ready', label: '장바구니까지' },
  { value: 'pre_payment', label: '결제 직전까지' },
];

const AUTH_CHALLENGE_OPTIONS = [
  { value: 'user_handles', label: '발생 시 사용자 처리' },
  { value: 'session_reuse', label: '로그인 세션 유지 우선' },
  { value: 'manual_only', label: '인증 발생 시 수동 전환' },
];

const APPROVAL_MODE_OPTIONS = [
  { value: 'review', label: '검토 후 발송' },
  { value: 'auto_candidate', label: '자동 발송 후보' },
];

const FALLBACK_MODE_OPTIONS = [
  { value: 'hold', label: '응답 없으면 보류' },
  { value: 'phone', label: '전화 확인으로 전환' },
  { value: 'manual', label: '수기 처리로 전환' },
];

const AUTO_ENABLED_OPTIONS = [
  { value: '사용', label: '사용' },
  { value: '미사용', label: '미사용' },
];

function metadataChoice(
  metadata: Record<string, unknown> | undefined,
  key: string,
  fallback: string,
  options: Array<{ value: string; label: string }>,
): string {
  const value = metadata?.[key];
  if (typeof value !== 'string') return fallback;
  return options.some((o) => o.value === value) ? value : fallback;
}

function metadataString(
  metadata: Record<string, unknown> | undefined,
  key: string,
  fallback = '',
): string {
  const value = metadata?.[key];
  return typeof value === 'string' ? value : fallback;
}

function metadataBoolean(
  metadata: Record<string, unknown> | undefined,
  key: string,
  fallback = false,
): boolean {
  const value = metadata?.[key];
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value === 'true';
  if (typeof value === 'number') return value !== 0;
  return fallback;
}

function supplierAutomationSettings(
  metadata: Record<string, unknown> | undefined,
): SupplierAutomationSettings {
  return {
    orderChannel: metadataChoice(metadata, 'autoOrderChannel', 'sms', ORDER_CHANNEL_OPTIONS),
    paymentMode: metadataChoice(metadata, 'paymentMode', 'none', PAYMENT_MODE_OPTIONS),
    approvalMode: metadataChoice(metadata, 'approvalMode', 'review', APPROVAL_MODE_OPTIONS),
    fallbackMode: metadataChoice(metadata, 'fallbackMode', 'hold', FALLBACK_MODE_OPTIONS),
  };
}

function supplierDesignSettings(supplier: SupplierRow | null | undefined): SupplierDesignSettings {
  const metadata = supplier?.metadata;
  const automation = supplierAutomationSettings(metadata);
  const channelType = metadataChoice(metadata, 'channelType', 'offline', CHANNEL_TYPE_OPTIONS);
  const channelOptions = channelType === 'online' ? ONLINE_CHANNEL_OPTIONS : OFFLINE_CHANNEL_OPTIONS;
  const channelFallback = channelType === 'online' ? 'personal_mall' : automation.orderChannel;
  const onlineFallback = channelType === 'online';
  return {
    ...automation,
    channelType,
    channel: metadataChoice(metadata, 'channel', channelFallback, channelOptions),
    channelAccount: metadataString(metadata, 'channelAccount', supplier?.contactName ?? ''),
    channelTarget: metadataString(metadata, 'channelTarget', supplier?.smsRecipient ?? supplier?.phone ?? ''),
    orderMethod: metadataString(
      metadata,
      'orderMethod',
      channelType === 'online'
        ? '장바구니 담기 후 승인 발주'
        : '[자동발주] {품목} {수량} 발주 요청',
    ),
    connectorMode: metadataString(
      metadata,
      'connectorMode',
      channelType === 'online' ? 'MCP/커넥터 예정' : '문자 발송 업체 연동 예정',
    ),
    defaultOrderTime: metadataString(metadata, 'defaultOrderTime', '22:30'),
    executionTarget: metadataChoice(
      metadata,
      'executionTarget',
      channelType === 'online' ? 'user_mobile' : 'desktop_web',
      EXECUTION_TARGET_OPTIONS,
    ),
    mobileOpenBehavior: metadataChoice(
      metadata,
      'mobileOpenBehavior',
      'unknown',
      MOBILE_OPEN_BEHAVIOR_OPTIONS,
    ),
    automationScope: metadataChoice(
      metadata,
      'automationScope',
      channelType === 'online' ? 'handoff_only' : 'search_ready',
      AUTOMATION_SCOPE_OPTIONS,
    ),
    authChallengeHandling: metadataChoice(
      metadata,
      'authChallengeHandling',
      'user_handles',
      AUTH_CHALLENGE_OPTIONS,
    ),
    searchableProducts: metadataBoolean(metadata, 'searchableProducts', onlineFallback),
    orderHistoryAvailable: metadataBoolean(metadata, 'orderHistoryAvailable', onlineFallback),
    directUrlOpen: metadataBoolean(metadata, 'directUrlOpen', onlineFallback),
    optionSelectable: metadataBoolean(metadata, 'optionSelectable', onlineFallback),
    alwaysLoggedIn: metadataBoolean(metadata, 'alwaysLoggedIn', onlineFallback),
    smsVerificationRequired: metadataBoolean(metadata, 'smsVerificationRequired', onlineFallback),
    emailVerificationRequired: metadataBoolean(metadata, 'emailVerificationRequired', false),
    googleVerificationRequired: metadataBoolean(metadata, 'googleVerificationRequired', false),
    otherVerificationRequired: metadataBoolean(metadata, 'otherVerificationRequired', false),
    paymentPasswordRequired: metadataBoolean(metadata, 'paymentPasswordRequired', onlineFallback),
    cardRegistered: metadataBoolean(metadata, 'cardRegistered', onlineFallback),
  };
}

function optionLabel(options: Array<{ value: string; label: string }>, value: string): string {
  return options.find((o) => o.value === value)?.label ?? value;
}

function normalizeOrderTime(value: string): string | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function readString(source: Record<string, unknown>, key: string, fallback = ''): string {
  const value = source[key];
  return typeof value === 'string' ? value.trim() : fallback;
}

function readNullableString(source: Record<string, unknown>, key: string): string | null {
  const value = readString(source, key);
  return value || null;
}

function readNumberOrNull(source: Record<string, unknown>, key: string): number | null {
  const value = Number(source[key]);
  return Number.isFinite(value) ? value : null;
}

function materialOrderUnit(row: InventoryMaterialRow): string {
  return row.comparableUnit || row.providerUnit || row.unit || '개';
}

function formatAmount(value: number): string {
  return value.toLocaleString('ko-KR', { maximumFractionDigits: 4 });
}

function normalizeSupplyItemKey(value: string | null | undefined): string {
  return (value ?? '').trim().toLocaleLowerCase();
}

function supplyItemDedupeKey(item: SupplyItem): string {
  if (item.sourceId) return `source:${item.sourceId}`;
  if (item.providerKind && item.providerItemId) {
    return `provider:${item.providerKind}:${item.providerItemId}`;
  }
  const name = normalizeSupplyItemKey(item.sourceName || item.internalName || item.supplierName);
  const location = normalizeSupplyItemKey(item.sourceLocationId || item.sourceLocationName);
  return `legacy:${location}:${name}`;
}

function dedupeSupplyItems(items: SupplyItem[]): SupplyItem[] {
  const seen = new Set<string>();
  const next: SupplyItem[] = [];
  items.forEach((item) => {
    const key = supplyItemDedupeKey(item);
    if (seen.has(key)) return;
    seen.add(key);
    next.push(item);
  });
  return next;
}

function mergeSupplyItems(current: SupplyItem[], incoming: SupplyItem[]): SupplyItem[] {
  return dedupeSupplyItems([...current, ...incoming]);
}

function supplyItemsFromMetadata(metadata: Record<string, unknown> | undefined): SupplyItem[] {
  const raw = metadata?.supplyItems;
  if (!Array.isArray(raw)) return [];
  return dedupeSupplyItems(raw
    .map((item) => normalizeSupplyItem(item))
    .filter((item): item is SupplyItem => item !== null));
}

function normalizeSupplyItem(raw: unknown): SupplyItem | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const source = raw as Record<string, unknown>;
  const internalName = readString(source, 'internalName');
  const supplierName = readString(source, 'supplierName');
  const unit = readString(source, 'unit');
  const sourceId = readNullableString(source, 'sourceId');
  const sourceName = readString(source, 'sourceName', internalName || supplierName);
  const sourceUnit = readString(source, 'sourceUnit', unit || '개');
  const orderUnit = readString(source, 'orderUnit', unit || sourceUnit || '개');
  const defaultQty = Number(source.defaultQty);
  const safetyStock = Number(source.safetyStock);
  const autoEnabled = source.autoEnabled === '미사용' ? '미사용' : '사용';
  const memo = typeof source.memo === 'string' ? source.memo : '';
  if (!internalName && !supplierName && !sourceName) return null;
  return {
    internalName: internalName || sourceName || supplierName,
    supplierName: supplierName || internalName || sourceName,
    unit: unit || orderUnit,
    defaultQty: Number.isFinite(defaultQty) && defaultQty > 0 ? defaultQty : 1,
    safetyStock: Number.isFinite(safetyStock) && safetyStock >= 0 ? safetyStock : 0,
    autoEnabled,
    memo,
    sourceKind: source.sourceKind === 'inventory' || sourceId ? 'inventory' : null,
    sourceId,
    sourceName: sourceName || internalName || supplierName,
    sourceUnit,
    sourceLocationId: readNullableString(source, 'sourceLocationId'),
    sourceLocationName: readNullableString(source, 'sourceLocationName'),
    providerKind:
      source.providerKind === 'boxhero' || source.providerKind === 'dvvb'
        ? source.providerKind
        : null,
    providerItemId: readNullableString(source, 'providerItemId'),
    orderUnit,
    conversionRatio: readNumberOrNull(source, 'conversionRatio'),
  };
}

function emptySupplyItem(): SupplyItem {
  return {
    internalName: '',
    supplierName: '',
    unit: '개',
    defaultQty: 1,
    safetyStock: 0,
    autoEnabled: '사용',
    memo: '',
    sourceKind: null,
    sourceId: null,
    sourceName: '',
    sourceUnit: '개',
    sourceLocationId: null,
    sourceLocationName: null,
    providerKind: null,
    providerItemId: null,
    orderUnit: '개',
    conversionRatio: null,
  };
}

function supplyItemFromMaterial(row: InventoryMaterialRow): SupplyItem {
  const orderUnit = materialOrderUnit(row);
  return {
    internalName: row.name,
    supplierName: row.name,
    unit: orderUnit,
    defaultQty: 1,
    safetyStock: Math.max(0, Number(row.minStock) || 0),
    autoEnabled: '사용',
    memo: '',
    sourceKind: 'inventory',
    sourceId: row.id,
    sourceName: row.name,
    sourceUnit: row.unit || orderUnit,
    sourceLocationId: row.locationId,
    sourceLocationName: row.locationName,
    providerKind: row.providerKind,
    providerItemId: row.providerItemId,
    orderUnit,
    conversionRatio: row.conversionRatio,
  };
}

function withSupplierMetadata(
  current: Record<string, unknown> | undefined,
  settings: SupplierDesignSettings,
  supplyItems?: SupplyItem[],
): Record<string, unknown> {
  return {
    ...(current ?? {}),
    autoOrderChannel: settings.orderChannel,
    paymentMode: settings.paymentMode,
    approvalMode: settings.approvalMode,
    fallbackMode: settings.fallbackMode,
    channelType: settings.channelType,
    channel: settings.channel,
    channelAccount: settings.channelAccount,
    channelTarget: settings.channelTarget,
    orderMethod: settings.orderMethod,
    connectorMode: settings.connectorMode,
    defaultOrderTime: settings.defaultOrderTime,
    executionTarget: settings.executionTarget,
    mobileOpenBehavior: settings.mobileOpenBehavior,
    automationScope: settings.automationScope,
    authChallengeHandling: settings.authChallengeHandling,
    searchableProducts: settings.searchableProducts,
    orderHistoryAvailable: settings.orderHistoryAvailable,
    directUrlOpen: settings.directUrlOpen,
    optionSelectable: settings.optionSelectable,
    alwaysLoggedIn: settings.alwaysLoggedIn,
    smsVerificationRequired: settings.smsVerificationRequired,
    emailVerificationRequired: settings.emailVerificationRequired,
    googleVerificationRequired: settings.googleVerificationRequired,
    otherVerificationRequired: settings.otherVerificationRequired,
    paymentPasswordRequired: settings.paymentPasswordRequired,
    cardRegistered: settings.cardRegistered,
    ...(supplyItems ? { supplyItems: dedupeSupplyItems(supplyItems) } : {}),
  };
}

function supplierUpdateBody(
  supplier: SupplierRow,
  metadata: Record<string, unknown>,
  overrides: { autoOrderEnabled?: boolean } = {},
): Record<string, unknown> {
  return {
    name: supplier.name,
    phone: supplier.phone,
    address: supplier.address,
    contactName: supplier.contactName,
    autoOrderEnabled: overrides.autoOrderEnabled ?? supplier.autoOrderEnabled,
    defaultLeadTimeDays: supplier.defaultLeadTimeDays ?? 1,
    smsRecipient: supplier.smsRecipient ?? null,
    metadata,
  };
}

function channelOptions(channelType: string): Array<{ value: string; label: string }> {
  return channelType === 'online' ? ONLINE_CHANNEL_OPTIONS : OFFLINE_CHANNEL_OPTIONS;
}

function formatWon(value: string | null | undefined): string {
  if (!value) return '—';
  const num = Number(value);
  return Number.isFinite(num) ? `₩${Math.round(num).toLocaleString('ko-KR')}` : '—';
}

function boolText(value: boolean): string {
  return value ? '가능' : '불가';
}

async function callDvvbApi<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json; charset=utf-8');
  }
  const token = getStoredToken();
  if (token) {
    headers.set('authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
  });
  const data = await response.json().catch(() => null);
  return unwrap<T>({ data, response });
}

function isCoupangSupplierSettings(supplier: SupplierRow, settings: SupplierDesignSettings): boolean {
  if (settings.channelType !== 'online') return false;
  const text = [
    settings.channel,
    settings.channelTarget,
    settings.channelAccount,
    supplier.name,
  ].join(' ').toLowerCase();
  return settings.channel === 'coupang' || text.includes('coupang') || text.includes('쿠팡');
}

function onlineSessionStatusLabel(status: OnlineOrderSessionAuthStatus | undefined): string {
  const labels: Record<OnlineOrderSessionAuthStatus, string> = {
    unknown: '미확인',
    login_required: '로그인 필요',
    authenticated: '로그인됨',
    captcha_required: '보안 확인 필요',
    blocked: '차단됨',
  };
  return labels[status ?? 'unknown'];
}

function onlineSessionBadgeVariant(
  status: OnlineOrderSessionAuthStatus | undefined,
): 'success' | 'warning' | 'danger' | 'info' {
  if (status === 'authenticated') return 'success';
  if (status === 'blocked' || status === 'captcha_required') return 'danger';
  if (status === 'login_required') return 'warning';
  return 'info';
}

function authMethodsLabel(settings: SupplierPlatformProfile): string {
  const methods = [
    settings.smsVerificationRequired ? 'SMS' : '',
    settings.emailVerificationRequired ? '이메일' : '',
    settings.googleVerificationRequired ? 'Google' : '',
    settings.otherVerificationRequired ? '기타' : '',
  ].filter(Boolean);
  return methods.length > 0 ? methods.join(', ') : '미확인';
}

function leadTimeLabel(days: number | undefined): string {
  const safe = Number.isFinite(days) ? Number(days) : 1;
  if (safe <= 0) return '당일';
  return `${safe}일`;
}

export function SettingsSuppliersRoute() {
  return <SupplierManagementPanel surface="settings" />;
}

export function SupplierManagementPanel({
  surface = 'settings',
}: {
  surface?: 'settings' | 'inventory';
}) {
  const auth = useAuth();
  const isOwner = auth.role === 'owner';
  const toast = useToast();
  const viewport = useViewport();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [modalState, setModalState] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; supplier: SupplierRow }
    | null
  >(null);
  const [supplyModal, setSupplyModal] = useState<
    | { mode: 'create'; supplier: SupplierRow }
    | { mode: 'edit'; supplier: SupplierRow; index: number; item: SupplyItem }
    | null
  >(null);

  const listQ = useQuery({
    queryKey: ['suppliers', 'list'],
    queryFn: async () => {
      const r = await suppliersControllerList({
        query: { limit: '500' } as never,
      });
      return unwrap<SuppliersEnvelope>(r);
    },
  });

  const suppliers = listQ.data?.items ?? [];

  useEffect(() => {
    if (activeId && suppliers.some((s) => s.id === activeId)) return;
    if (suppliers.length > 0) setActiveId(suppliers[0]!.id);
  }, [suppliers, activeId]);

  const active = useMemo(
    () => suppliers.find((s) => s.id === activeId) ?? null,
    [suppliers, activeId],
  );

  const removeMu = useMutation({
    mutationFn: async (id: string) => {
      const r = await suppliersControllerRemove({ path: { id } });
      return unwrap<{ deleted: boolean; id: string }>(r);
    },
    onSuccess: async () => {
      toast.success('거래처를 삭제했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '거래처 삭제 실패');
    },
  });

  const supplyItemsMu = useMutation({
    mutationFn: async ({
      supplier,
      items,
    }: {
      supplier: SupplierRow;
      items: SupplyItem[];
    }) => {
      const metadata = {
        ...(supplier.metadata ?? {}),
        supplyItems: dedupeSupplyItems(items),
      };
      const r = await suppliersControllerUpdate({
        path: { id: supplier.id },
        body: supplierUpdateBody(supplier, metadata) as never,
      });
      return unwrap<{ item: SupplierRow }>(r);
    },
    onSuccess: async () => {
      toast.success('공급 물품 매핑을 저장했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      setSupplyModal(null);
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '공급 물품 저장 실패');
    },
  });

  const autoSettingsMu = useMutation({
    mutationFn: async ({
      supplier,
      autoOrderEnabled,
      defaultOrderTime,
    }: {
      supplier: SupplierRow;
      autoOrderEnabled: boolean;
      defaultOrderTime: string;
    }) => {
      const settings = supplierDesignSettings(supplier);
      const metadata = withSupplierMetadata(
        supplier.metadata,
        { ...settings, defaultOrderTime },
        supplyItemsFromMetadata(supplier.metadata),
      );
      const r = await suppliersControllerUpdate({
        path: { id: supplier.id },
        body: supplierUpdateBody(supplier, metadata, { autoOrderEnabled }) as never,
      });
      return unwrap<{ item: SupplierRow }>(r);
    },
    onSuccess: async () => {
      toast.success('자동발주 설정을 저장했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '자동발주 설정 저장 실패');
    },
  });

  if (listQ.isLoading) {
    return (
      <div style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}>
        거래처 목록을 불러오는 중…
      </div>
    );
  }

  if (listQ.isError) {
    return (
      <AlertBanner
        tone="danger"
        message={`거래처 목록을 불러오지 못했습니다: ${
          (listQ.error as ApiCallError | undefined)?.message ?? '알 수 없는 오류'
        }`}
      />
    );
  }

  return (
    <div className="dvvb-supplier-root" style={{ display: 'grid', gap: 16 }}>
      <SectionHead
        title="거래처"
        sub={
          surface === 'inventory'
            ? '거래처별 공급 물품과 발주 채널을 등록해야 재고 부족 항목이 자동발주 대기 리스트로 넘어갑니다.'
            : '발주 거래처, 공급 물품 매핑, 자동발주 채널 기준을 한 화면에서 관리합니다.'
        }
        action={
          <Button
            variant="primary"
            size="md"
            disabled={!isOwner}
            title={!isOwner ? '권한 없음 (owner 전용)' : ''}
            onClick={() => setModalState({ mode: 'create' })}
          >
            <Icon name="plus" size={14} />
            거래처 추가
          </Button>
        }
      />

      {suppliers.length === 0 ? (
        <EmptyState
          icon="truck"
          title="등록된 거래처가 없습니다"
          description="자동발주를 사용하려면 거래처와 공급 물품을 먼저 등록하세요."
          action={
            isOwner ? (
              <Button variant="primary" onClick={() => setModalState({ mode: 'create' })}>
                + 거래처 추가
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="dvvb-supplier-layout">
          <SuppliersList suppliers={suppliers} activeId={activeId} onSelect={setActiveId} />
          {active ? (
            <SupplierDetail
              supplier={active}
              isOwner={isOwner}
              onEdit={() => setModalState({ mode: 'edit', supplier: active })}
              onRemove={() => {
                if (!window.confirm(`"${active.name}" 거래처를 삭제하시겠습니까? 자동발주 대기 카드도 함께 제거됩니다.`)) {
                  return;
                }
                removeMu.mutate(active.id);
              }}
              removing={removeMu.isPending}
              onAddSupply={() => setSupplyModal({ mode: 'create', supplier: active })}
              onEditSupply={(index, item) =>
                setSupplyModal({ mode: 'edit', supplier: active, index, item })
              }
              onDeleteSupply={(index, item) => {
                if (!window.confirm(`${item.sourceName || item.internalName} 공급 물품을 삭제할까요? 자동발주 후보에서도 빠집니다.`)) {
                  return;
                }
                const next = supplyItemsFromMetadata(active.metadata).filter((_, i) => i !== index);
                supplyItemsMu.mutate({ supplier: active, items: next });
              }}
              savingSupply={supplyItemsMu.isPending}
              savingAutoSettings={autoSettingsMu.isPending}
              onSaveAutoSettings={(autoOrderEnabled, defaultOrderTime) =>
                autoSettingsMu.mutate({ supplier: active, autoOrderEnabled, defaultOrderTime })
              }
              viewport={viewport}
            />
          ) : (
            <EmptyState
              icon="truck"
              title="거래처를 선택하세요"
              description="좌측에서 거래처를 클릭하면 상세 정보를 볼 수 있습니다."
            />
          )}
        </div>
      )}

      {modalState && (
        <SupplierFormModal
          state={modalState}
          isOwner={isOwner}
          onClose={() => setModalState(null)}
          onSaved={(savedId) => {
            setModalState(null);
            if (savedId) setActiveId(savedId);
          }}
        />
      )}

      {supplyModal && (
        <SupplyItemModal
          state={supplyModal}
          isOwner={isOwner}
          submitting={supplyItemsMu.isPending}
          onClose={() => setSupplyModal(null)}
          onSaved={(items) => {
            const currentItems = supplyItemsFromMetadata(supplyModal.supplier.metadata);
            const next =
              supplyModal.mode === 'edit'
                ? dedupeSupplyItems(
                    currentItems.map((it, i) =>
                      i === supplyModal.index ? items[0] ?? it : it,
                    ),
                  )
                : mergeSupplyItems(currentItems, items);
            supplyItemsMu.mutate({ supplier: supplyModal.supplier, items: next });
          }}
        />
      )}
    </div>
  );
}

function SuppliersList({
  suppliers,
  activeId,
  onSelect,
}: {
  suppliers: SupplierRow[];
  activeId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <aside className="dvvb-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="dvvb-panel-head">
        <h3 className="dvvb-h3">등록된 거래처 ({suppliers.length})</h3>
        <div className="dvvb-copy" style={{ marginTop: 4 }}>
          자동발주 원천이 되는 거래처 목록입니다.
        </div>
      </div>
      <div className="dvvb-supplier-list">
        {suppliers.map((supplier) => {
          const design = supplierDesignSettings(supplier);
          const selected = supplier.id === activeId;
          return (
            <button
              key={supplier.id}
              type="button"
              className={`dvvb-listitem${selected ? ' is-active' : ''}`}
              onClick={() => onSelect(supplier.id)}
            >
              <span className="dvvb-listitem-title">{supplier.name}</span>
              <span className="dvvb-listitem-sub">
                {optionLabel(channelOptions(design.channelType), design.channel)} ·{' '}
                {supplier.smsRecipient || supplier.phone || '연락처 없음'}
              </span>
              {supplier.autoOrderEnabled && (
                <span>
                  <Badge variant="success" dot>
                    자동발주
                  </Badge>
                </span>
              )}
            </button>
          );
        })}
      </div>
    </aside>
  );
}

function SupplierDetail({
  supplier,
  isOwner,
  onEdit,
  onRemove,
  removing,
  onAddSupply,
  onEditSupply,
  onDeleteSupply,
  savingSupply,
  savingAutoSettings,
  onSaveAutoSettings,
  viewport,
}: {
  supplier: SupplierRow;
  isOwner: boolean;
  onEdit: () => void;
  onRemove: () => void;
  removing: boolean;
  onAddSupply: () => void;
  onEditSupply: (index: number, item: SupplyItem) => void;
  onDeleteSupply: (index: number, item: SupplyItem) => void;
  savingSupply: boolean;
  savingAutoSettings: boolean;
  onSaveAutoSettings: (autoOrderEnabled: boolean, defaultOrderTime: string) => void;
  viewport: 'mobile' | 'tablet' | 'desktop';
}) {
  const settings = supplierDesignSettings(supplier);
  const supplyItems = supplyItemsFromMetadata(supplier.metadata);

  return (
    <section className="dvvb-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="dvvb-panel-head">
        <div className="dvvb-section-head">
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 className="dvvb-title">{supplier.name}</h2>
            <div className="dvvb-copy" style={{ marginTop: 4 }}>
              {supplier.address || settings.channelTarget || '주소/발주처 미등록'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Button
              variant="ghost"
              size="sm"
              disabled={!isOwner}
              title={!isOwner ? '권한 없음 (owner 전용)' : ''}
              onClick={onEdit}
            >
              <Icon name="edit" size={13} />
              편집
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={!isOwner || removing}
              title={!isOwner ? '권한 없음 (owner 전용)' : ''}
              onClick={onRemove}
              style={{ color: 'var(--danger)' }}
            >
              <Icon name="trash" size={13} />
              삭제
            </Button>
          </div>
        </div>
        <div className="dvvb-copy" style={{ marginTop: 10 }}>
          등록된 거래처 상세 정보, 발주 채널, 자동발주로 넘길 공급 물품 매핑을 확인합니다.
        </div>
      </div>

      <div className="dvvb-panel-body">
        <div className="dvvb-info-grid">
          <InfoField label="상점명" value={supplier.name} />
          <InfoField label="전화번호" value={supplier.phone ?? '—'} />
          <InfoField label="주소" value={supplier.address ?? '—'} wide />
          <InfoField label="담당자" value={supplier.contactName ?? '—'} />
          <InfoField label="이번달 발주" value={formatWon(supplier.monthlyVolumeWon)} />
          <InfoField label="리드타임" value={leadTimeLabel(supplier.defaultLeadTimeDays)} />
          <InfoField
            label="주 발주 채널"
            value={`${optionLabel(CHANNEL_TYPE_OPTIONS, settings.channelType)} / ${optionLabel(
              channelOptions(settings.channelType),
              settings.channel,
            )}`}
            wide
          />
        </div>

        <ChannelView supplier={supplier} settings={settings} />
        {isOwner && isCoupangSupplierSettings(supplier, settings) && (
          <CoupangSupplierSessionPanel supplier={supplier} isOwner={isOwner} />
        )}
        <AutoSettingStrip
          supplier={supplier}
          settings={settings}
          isOwner={isOwner}
          saving={savingAutoSettings}
          onSave={onSaveAutoSettings}
        />

        <div className="dvvb-banner">
          <Icon name="alert" size={16} color="var(--info)" />
          <div>
            <b>자동발주 원천</b> — 거래처 정보 확인 후 공급 물품을 등록/매핑한 항목만
            재고 부족 푸시를 통해 자동발주 대기 리스트로 이동합니다.
          </div>
        </div>

        <SupplyItemMapping
          supplier={supplier}
          items={supplyItems}
          isOwner={isOwner}
          saving={savingSupply}
          onAdd={onAddSupply}
          onEdit={onEditSupply}
          onDelete={onDeleteSupply}
          viewport={viewport}
        />
      </div>
    </section>
  );
}

function CoupangSupplierSessionPanel({
  supplier,
  isOwner,
}: {
  supplier: SupplierRow;
  isOwner: boolean;
}) {
  const toast = useToast();
  const pendingBrowserWindow = useRef<Window | null>(null);
  const sessionQ = useQuery({
    queryKey: ['online-order-runner', 'supplier-session', supplier.id],
    queryFn: () =>
      callDvvbApi<OnlineOrderRunnerSessionResponse>(
        `/v1/online-order-runner/suppliers/${supplier.id}/session/status`,
      ),
    retry: false,
  });

  const openBrowserMu = useMutation({
    mutationFn: () =>
      callDvvbApi<OnlineOrderRunnerSessionResponse>(
        `/v1/online-order-runner/suppliers/${supplier.id}/session/browser/open`,
        {
          method: 'POST',
          body: JSON.stringify({}),
        },
      ),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({
        queryKey: ['online-order-runner', 'supplier-session', supplier.id],
      });
      const browserUrl = res.browser?.url ?? res.item.browserUrl;
      const openedWindow = pendingBrowserWindow.current;
      pendingBrowserWindow.current = null;
      if (browserUrl) {
        if (openedWindow && !openedWindow.closed) {
          openedWindow.location.href = browserUrl;
        } else {
          window.open(browserUrl, '_blank', 'noopener,noreferrer');
        }
        toast.info('쿠팡 로그인 창을 열었습니다.');
      } else {
        openedWindow?.close();
        toast.error('쿠팡 로그인 창 URL을 받지 못했습니다.');
      }
    },
    onError: (e) => {
      pendingBrowserWindow.current?.close();
      pendingBrowserWindow.current = null;
      const err = e as ApiCallError;
      toast.error(err.message ?? '쿠팡 로그인 창 열기 실패');
    },
  });

  const closeBrowserMu = useMutation({
    mutationFn: () =>
      callDvvbApi<OnlineOrderRunnerSessionResponse>(
        `/v1/online-order-runner/suppliers/${supplier.id}/session/browser/close`,
        {
          method: 'POST',
          body: JSON.stringify({}),
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['online-order-runner', 'supplier-session', supplier.id],
      });
      toast.info('쿠팡 로그인 창을 닫았습니다.');
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '쿠팡 로그인 창 닫기 실패');
    },
  });

  const checkMu = useMutation({
    mutationFn: () =>
      callDvvbApi<OnlineOrderRunnerSessionResponse>(
        `/v1/online-order-runner/suppliers/${supplier.id}/session/status?check=1`,
      ),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({
        queryKey: ['online-order-runner', 'supplier-session', supplier.id],
      });
      toast.info(`쿠팡 세션 상태: ${onlineSessionStatusLabel(res.item.authStatus)}`);
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '쿠팡 세션 확인 실패');
    },
  });

  const session = (
    checkMu.data?.item
    ?? closeBrowserMu.data?.item
    ?? openBrowserMu.data?.item
    ?? sessionQ.data?.item
  );
  const status = session?.authStatus ?? 'unknown';
  const busy = sessionQ.isFetching || openBrowserMu.isPending || closeBrowserMu.isPending || checkMu.isPending;
  const browserControl = openBrowserMu.data?.browser;
  const blockers = [
    ...(session?.blockers?.filter(Boolean) ?? []),
    ...(browserControl?.blockers?.filter(Boolean) ?? []),
  ];

  return (
    <section className="dvvb-box">
      <div className="dvvb-section-head" style={{ marginBottom: 12 }}>
        <div>
          <h3 className="dvvb-h3">쿠팡 로그인 세션</h3>
          <div className="dvvb-copy" style={{ marginTop: 4 }}>
            거래처에 연결된 쿠팡 runner 프로필 상태입니다.
          </div>
        </div>
        <Badge variant={onlineSessionBadgeVariant(status)} dot>
          {onlineSessionStatusLabel(status)}
        </Badge>
      </div>
      <div className="dvvb-info-grid" style={{ marginBottom: 12 }}>
        <InfoField label="세션 ID" value={session?.sessionId ?? `coupang-${supplier.id}`} />
        <InfoField label="로그인 URL" value={session?.loginUrl ?? 'https://login.coupang.com/login/login.pang'} wide />
        <InfoField label="브라우저 화면" value={browserControl?.url ?? session?.browserUrl ?? '—'} wide />
        <InfoField label="마지막 열림" value={session?.lastOpenedUrl ?? '—'} wide />
        <InfoField label="차단 사유" value={blockers.length > 0 ? blockers.join(', ') : '—'} wide />
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button
          variant="primary"
          size="sm"
          disabled={!isOwner || busy}
          title={!isOwner ? '권한 없음 (owner 전용)' : ''}
          onClick={() => {
            pendingBrowserWindow.current = window.open('about:blank', 'dvvb-coupang-runner');
            openBrowserMu.mutate();
          }}
        >
          <Icon name="logout" size={13} />
          쿠팡 로그인 창 열기
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={!isOwner || busy || session?.browserStatus !== 'open'}
          title={!isOwner ? '권한 없음 (owner 전용)' : ''}
          onClick={() => closeBrowserMu.mutate()}
        >
          <Icon name="x" size={13} />
          창 닫기
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={busy}
          onClick={() => checkMu.mutate()}
        >
          <Icon name="refresh" size={13} />
          상태 확인
        </Button>
      </div>
    </section>
  );
}

function InfoField({ label, value, wide }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={`dvvb-field-card${wide ? ' is-wide' : ''}`}>
      <div className="dvvb-field-label">{label}</div>
      <div className="dvvb-field-value">{value || '—'}</div>
    </div>
  );
}

function ChannelView({
  supplier,
  settings,
}: {
  supplier: SupplierRow;
  settings: SupplierDesignSettings;
}) {
  const isOnline = settings.channelType === 'online';
  const options = channelOptions(settings.channelType);
  return (
    <section className="dvvb-box">
      <div className="dvvb-section-head" style={{ marginBottom: 12 }}>
        <div>
          <h3 className="dvvb-h3">거래처 등록 방식 / 발주 채널</h3>
          <div className="dvvb-copy" style={{ marginTop: 4 }}>
            거래처마다 온라인 플랫폼형과 오프라인 발주수단형을 분리해서 관리합니다.
          </div>
        </div>
        <Badge variant="warning">추후 MCP/커넥터 가능</Badge>
      </div>
      <div className="dvvb-channel-card">
        <h4 className="dvvb-h3">
          {isOnline ? '온라인 거래처 등록 정보' : '오프라인 거래처 등록 정보'}
        </h4>
        <div className="dvvb-channel-options">
          {options.map((option) => (
            <span
              key={option.value}
              className={`dvvb-channel-option${
                option.value === settings.channel ? ' is-active' : ''
              }`}
            >
              {option.label}
            </span>
          ))}
        </div>
        <div className="dvvb-info-grid">
          <InfoField label="등록 방식" value={optionLabel(CHANNEL_TYPE_OPTIONS, settings.channelType)} />
          <InfoField label="주 발주 채널" value={optionLabel(options, settings.channel)} />
          <InfoField
            label={isOnline ? '플랫폼 계정 / 발주처명' : '수신 담당자 / 채널명'}
            value={settings.channelAccount || supplier.contactName || '—'}
          />
          <InfoField
            label={isOnline ? '플랫폼 / 몰 주소' : '전화번호 / 카카오 채널 / 이메일'}
            value={settings.channelTarget || supplier.smsRecipient || supplier.phone || '—'}
          />
          <InfoField
            label={isOnline ? '온라인 발주 방식' : '발주 메시지 템플릿'}
            value={settings.orderMethod}
            wide
          />
          <InfoField label={isOnline ? '온라인 연동 방식' : '발송 연동 방식'} value={settings.connectorMode} />
          <InfoField label="결제 처리" value={optionLabel(PAYMENT_MODE_OPTIONS, settings.paymentMode)} />
          <InfoField
            label="실패 시 대체 수단"
            value={optionLabel(FALLBACK_MODE_OPTIONS, settings.fallbackMode)}
            wide
          />
        </div>
        <div className="dvvb-note">
          {isOnline
            ? '온라인 거래처는 플랫폼 정보, 커넥터, 결제 처리까지 묶어서 관리합니다. 네이버, 쿠팡, 개인몰 연동은 추후 MCP/커넥터 기준으로 붙입니다.'
            : '오프라인 거래처는 문자, 카카오톡, 전화, 이메일처럼 실제 발주를 보낼 수신처와 메시지 양식을 관리합니다.'}
        </div>
        {isOnline && (
          <div className="dvvb-info-grid" style={{ marginTop: 12 }}>
            <InfoField
              label="실행 대상"
              value={optionLabel(EXECUTION_TARGET_OPTIONS, settings.executionTarget)}
            />
            <InfoField
              label="모바일 열림"
              value={optionLabel(MOBILE_OPEN_BEHAVIOR_OPTIONS, settings.mobileOpenBehavior)}
            />
            <InfoField
              label="자동화 범위"
              value={optionLabel(AUTOMATION_SCOPE_OPTIONS, settings.automationScope)}
            />
            <InfoField
              label="인증 처리"
              value={optionLabel(AUTH_CHALLENGE_OPTIONS, settings.authChallengeHandling)}
            />
            <InfoField label="검색 가능" value={boolText(settings.searchableProducts)} />
            <InfoField label="과거 주문내역" value={boolText(settings.orderHistoryAvailable)} />
            <InfoField label="URL 바로 열기" value={boolText(settings.directUrlOpen)} />
            <InfoField label="옵션 전부 선택" value={boolText(settings.optionSelectable)} />
            <InfoField label="항상 로그인" value={boolText(settings.alwaysLoggedIn)} />
            <InfoField label="인증 수단" value={authMethodsLabel(settings)} />
            <InfoField
              label="결제 비밀번호"
              value={settings.paymentPasswordRequired ? '필요' : '불필요'}
            />
            <InfoField label="카드 등록" value={settings.cardRegistered ? '등록됨' : '미등록'} />
          </div>
        )}
      </div>
    </section>
  );
}

function AutoSettingStrip({
  supplier,
  settings,
  isOwner,
  saving,
  onSave,
}: {
  supplier: SupplierRow;
  settings: SupplierDesignSettings;
  isOwner: boolean;
  saving: boolean;
  onSave: (autoOrderEnabled: boolean, defaultOrderTime: string) => void;
}) {
  const savedOrderTime = normalizeOrderTime(settings.defaultOrderTime) ?? '22:30';
  const [autoOrderEnabled, setAutoOrderEnabled] = useState(supplier.autoOrderEnabled);
  const [defaultOrderTime, setDefaultOrderTime] = useState(savedOrderTime);

  useEffect(() => {
    setAutoOrderEnabled(supplier.autoOrderEnabled);
    setDefaultOrderTime(savedOrderTime);
  }, [supplier.id, supplier.autoOrderEnabled, savedOrderTime]);

  const normalizedOrderTime = normalizeOrderTime(defaultOrderTime);
  const timeInvalid = defaultOrderTime.trim().length > 0 && !normalizedOrderTime;
  const dirty =
    autoOrderEnabled !== supplier.autoOrderEnabled ||
    (normalizedOrderTime ?? defaultOrderTime.trim()) !== savedOrderTime;
  const canSave = isOwner && dirty && !saving && !!normalizedOrderTime;

  return (
    <section className="dvvb-box">
      <div className="dvvb-section-head" style={{ marginBottom: 12 }}>
        <div>
          <h3 className="dvvb-h3">거래처별 자동발주 설정</h3>
          <div className="dvvb-copy" style={{ marginTop: 4 }}>
            거래처 상세에서 발주 후보 사용 여부와 기본 시간을 조정합니다. 발주서는 자동발주 화면에서 사용자가 직접 생성합니다.
          </div>
        </div>
      </div>
      <div className="dvvb-form-grid" style={{ marginBottom: 12 }}>
        <FormField label="자동발주 활성/비활성">
          <Toggle
            checked={autoOrderEnabled}
            onChange={setAutoOrderEnabled}
            label={autoOrderEnabled ? '활성' : '비활성'}
            disabled={!isOwner || saving}
          />
        </FormField>
        <FormField
          label="발주시간"
          hint="24시간 형식으로 입력합니다. 예: 22:30"
          error={timeInvalid ? '00:00~23:59 형식으로 입력하세요.' : undefined}
        >
          <TextInput
            value={defaultOrderTime}
            onChange={setDefaultOrderTime}
            placeholder="22:30"
            error={timeInvalid}
            disabled={!isOwner || saving}
            inputMode="numeric"
          />
        </FormField>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <Button
            variant="strong"
            size="sm"
            disabled={!canSave}
            title={!isOwner ? '권한 없음 (owner 전용)' : !dirty ? '변경된 설정 없음' : ''}
            onClick={() => {
              if (!normalizedOrderTime) return;
              onSave(autoOrderEnabled, normalizedOrderTime);
            }}
          >
            {saving ? '저장 중…' : '설정 저장'}
          </Button>
        </div>
      </div>
      <div className="dvvb-setting-strip">
        <SettingCell
          label="현재 저장값"
          value={supplier.autoOrderEnabled ? '자동발주 활성' : '자동발주 비활성'}
          good={supplier.autoOrderEnabled}
        />
        <SettingCell label="저장된 발주시간" value={savedOrderTime} />
        <SettingCell label="승인 방식" value={optionLabel(APPROVAL_MODE_OPTIONS, settings.approvalMode)} />
        <SettingCell label="미응답 처리" value={optionLabel(FALLBACK_MODE_OPTIONS, settings.fallbackMode)} />
      </div>
    </section>
  );
}

function SettingCell({ label, value, good }: { label: string; value: string; good?: boolean }) {
  return (
    <div className="dvvb-setting-cell">
      <span className="dvvb-setting-label">{label}</span>
      <span className={`dvvb-pill${good ? ' is-good' : ''}`}>{value}</span>
    </div>
  );
}

function SupplyItemMapping({
  supplier,
  items,
  isOwner,
  saving,
  onAdd,
  onEdit,
  onDelete,
  viewport,
}: {
  supplier: SupplierRow;
  items: SupplyItem[];
  isOwner: boolean;
  saving: boolean;
  onAdd: () => void;
  onEdit: (index: number, item: SupplyItem) => void;
  onDelete: (index: number, item: SupplyItem) => void;
  viewport: 'mobile' | 'tablet' | 'desktop';
}) {
  return (
    <section className="dvvb-box is-dashed">
      <div className="dvvb-section-head" style={{ marginBottom: 12 }}>
        <div>
          <h3 className="dvvb-h3">공급 물품 등록/매핑</h3>
          <div className="dvvb-copy" style={{ marginTop: 4 }}>
            제품 이름을 검색해 거래처 공급 물품과 우리 재료를 연결합니다. 발주단위와 수량은 자동발주에서 처리합니다.
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={!isOwner || saving}
          title={!isOwner ? '권한 없음 (owner 전용)' : ''}
          onClick={onAdd}
          style={viewport === 'mobile' ? { width: '100%' } : undefined}
        >
          <Icon name="plus" size={13} />
          공급 물품 추가
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="dvvb-note" style={{ marginTop: 0 }}>
          {supplier.name}에 등록된 공급 물품이 없습니다. 먼저 우리 재료를 선택해 공급 물품으로 연결해야 자동발주 대기 리스트로 넘어갑니다.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {items.map((item, index) => {
            const linked = !!item.sourceId;
            return (
              <div key={supplyItemDedupeKey(item)} className="dvvb-mapping-item">
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: 'var(--fg-1)', fontSize: 14, fontWeight: 800 }}>
                    {item.sourceName || item.internalName}
                  </div>
                  <div className="dvvb-copy" style={{ marginTop: 4 }}>
                    거래처 공급명: {item.supplierName}
                  </div>
                  <div className="dvvb-copy" style={{ marginTop: 3 }}>
                    {item.sourceLocationName ?? '사업장 미확인'}
                    {!linked
                      ? ' · 출처 미연결'
                      : item.providerKind === 'boxhero'
                        ? ' · BoxHero 연결 재료'
                        : ' · 직접 등록 재료'}
                  </div>
                  {item.memo && (
                    <div className="dvvb-copy" style={{ marginTop: 3 }}>
                      메모: {item.memo}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Badge variant={linked ? 'success' : 'warning'}>
                    {linked ? '재료 연결됨' : '재료 연결 필요'}
                  </Badge>
                  <span className={`dvvb-pill${item.autoEnabled === '사용' ? ' is-good' : ' is-bad'}`}>
                    자동발주 {item.autoEnabled}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!isOwner || saving}
                    onClick={() => onEdit(index, item)}
                  >
                    수정
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!isOwner || saving}
                    onClick={() => onDelete(index, item)}
                    style={{ color: 'var(--danger)' }}
                  >
                    삭제
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function SupplierFormModal({
  state,
  isOwner,
  onClose,
  onSaved,
}: {
  state: { mode: 'create' } | { mode: 'edit'; supplier: SupplierRow };
  isOwner: boolean;
  onClose: () => void;
  onSaved: (savedId: string | null) => void;
}) {
  const toast = useToast();
  const editing = state.mode === 'edit';
  const initial = editing ? state.supplier : null;
  const initialSettings = supplierDesignSettings(initial);

  const [name, setName] = useState(initial?.name ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [contactName, setContactName] = useState(initial?.contactName ?? '');
  const [autoOrder, setAutoOrder] = useState(initial?.autoOrderEnabled ?? true);
  const [leadTime, setLeadTime] = useState(String(initial?.defaultLeadTimeDays ?? 1));
  const [smsRecipient, setSmsRecipient] = useState(initial?.smsRecipient ?? '');
  const [channelType, setChannelType] = useState(initialSettings.channelType);
  const [channel, setChannel] = useState(initialSettings.channel);
  const [channelAccount, setChannelAccount] = useState(initialSettings.channelAccount);
  const [channelTarget, setChannelTarget] = useState(initialSettings.channelTarget);
  const [orderMethod, setOrderMethod] = useState(initialSettings.orderMethod);
  const [connectorMode, setConnectorMode] = useState(initialSettings.connectorMode);
  const [defaultOrderTime, setDefaultOrderTime] = useState(initialSettings.defaultOrderTime);
  const [executionTarget, setExecutionTarget] = useState(initialSettings.executionTarget);
  const [mobileOpenBehavior, setMobileOpenBehavior] = useState(
    initialSettings.mobileOpenBehavior,
  );
  const [automationScope, setAutomationScope] = useState(initialSettings.automationScope);
  const [authChallengeHandling, setAuthChallengeHandling] = useState(
    initialSettings.authChallengeHandling,
  );
  const [searchableProducts, setSearchableProducts] = useState(initialSettings.searchableProducts);
  const [orderHistoryAvailable, setOrderHistoryAvailable] = useState(
    initialSettings.orderHistoryAvailable,
  );
  const [directUrlOpen, setDirectUrlOpen] = useState(initialSettings.directUrlOpen);
  const [optionSelectable, setOptionSelectable] = useState(initialSettings.optionSelectable);
  const [alwaysLoggedIn, setAlwaysLoggedIn] = useState(initialSettings.alwaysLoggedIn);
  const [smsVerificationRequired, setSmsVerificationRequired] = useState(
    initialSettings.smsVerificationRequired,
  );
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(
    initialSettings.emailVerificationRequired,
  );
  const [googleVerificationRequired, setGoogleVerificationRequired] = useState(
    initialSettings.googleVerificationRequired,
  );
  const [otherVerificationRequired, setOtherVerificationRequired] = useState(
    initialSettings.otherVerificationRequired,
  );
  const [paymentPasswordRequired, setPaymentPasswordRequired] = useState(
    initialSettings.paymentPasswordRequired,
  );
  const [cardRegistered, setCardRegistered] = useState(initialSettings.cardRegistered);
  const [orderChannel, setOrderChannel] = useState(initialSettings.orderChannel);
  const [paymentMode, setPaymentMode] = useState(initialSettings.paymentMode);
  const [approvalMode, setApprovalMode] = useState(initialSettings.approvalMode);
  const [fallbackMode, setFallbackMode] = useState(initialSettings.fallbackMode);
  const [errors, setErrors] = useState<{ name?: string; leadTime?: string }>({});
  const prevChannelTypeRef = useRef(channelType);

  useEffect(() => {
    const options = channelOptions(channelType);
    if (!options.some((option) => option.value === channel)) {
      setChannel(options[0]?.value ?? '');
    }
  }, [channelType, channel]);

  useEffect(() => {
    if (prevChannelTypeRef.current !== 'online' && channelType === 'online') {
      setSearchableProducts(true);
      setOrderHistoryAvailable(true);
      setDirectUrlOpen(true);
      setOptionSelectable(true);
      setAlwaysLoggedIn(true);
      setExecutionTarget('user_mobile');
      setMobileOpenBehavior('unknown');
      setAutomationScope('handoff_only');
      setAuthChallengeHandling('user_handles');
      setSmsVerificationRequired(true);
      setEmailVerificationRequired(false);
      setGoogleVerificationRequired(false);
      setOtherVerificationRequired(false);
      setPaymentPasswordRequired(true);
      setCardRegistered(true);
    }
    prevChannelTypeRef.current = channelType;
  }, [channelType]);

  const designSettings: SupplierDesignSettings = {
    orderChannel,
    paymentMode,
    approvalMode,
    fallbackMode,
    channelType,
    channel,
    channelAccount,
    channelTarget,
    orderMethod,
    connectorMode,
    defaultOrderTime,
    executionTarget,
    mobileOpenBehavior,
    automationScope,
    authChallengeHandling,
    searchableProducts,
    orderHistoryAvailable,
    directUrlOpen,
    optionSelectable,
    alwaysLoggedIn,
    smsVerificationRequired,
    emailVerificationRequired,
    googleVerificationRequired,
    otherVerificationRequired,
    paymentPasswordRequired,
    cardRegistered,
  };

  const createMu = useMutation({
    mutationFn: async () => {
      const r = await suppliersControllerCreate({
        body: {
          name: name.trim(),
          phone: phone.trim() || undefined,
          address: address.trim() || undefined,
          contactName: contactName.trim() || undefined,
          autoOrderEnabled: autoOrder,
          defaultLeadTimeDays: Number(leadTime) || 1,
          smsRecipient: smsRecipient.trim() || undefined,
          metadata: withSupplierMetadata(undefined, designSettings, []),
        } as never,
      });
      return unwrap<{ item: SupplierRow }>(r);
    },
    onSuccess: async (res) => {
      toast.success('거래처를 추가했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      onSaved(res?.item?.id ?? null);
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '거래처 추가 실패');
    },
  });

  const updateMu = useMutation({
    mutationFn: async () => {
      if (!initial) throw new Error('no initial');
      const r = await suppliersControllerUpdate({
        path: { id: initial.id },
        body: {
          name: name.trim(),
          phone: phone.trim() || null,
          address: address.trim() || null,
          contactName: contactName.trim() || null,
          autoOrderEnabled: autoOrder,
          defaultLeadTimeDays: Number(leadTime) || 1,
          smsRecipient: smsRecipient.trim() || null,
          metadata: withSupplierMetadata(
            initial.metadata,
            designSettings,
            supplyItemsFromMetadata(initial.metadata),
          ),
        } as never,
      });
      return unwrap<{ item: SupplierRow }>(r);
    },
    onSuccess: async (res) => {
      toast.success('거래처를 저장했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      onSaved(res?.item?.id ?? null);
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '거래처 저장 실패');
    },
  });

  const submitting = createMu.isPending || updateMu.isPending;

  function submit() {
    const next: typeof errors = {};
    if (!name.trim()) next.name = '거래처명은 필수입니다.';
    const lt = Number(leadTime);
    if (!Number.isFinite(lt) || lt < 0 || lt > 30) {
      next.leadTime = '0~30 사이 정수로 입력하세요.';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    if (editing) updateMu.mutate();
    else createMu.mutate();
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={editing ? '거래처 수정' : '거래처 추가'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            취소
          </Button>
          <Button
            variant="strong"
            onClick={submit}
            disabled={submitting || !isOwner}
            title={!isOwner ? '권한 없음 (owner 전용)' : ''}
          >
            {submitting ? '저장 중…' : '저장'}
          </Button>
        </>
      }
      >
      <div style={{ display: 'grid', gap: 16 }}>
        <FormSection
          title="공통 폼"
          hint="거래처 공통 정보와 모든 발주 방식에서 공통으로 쓰는 값을 먼저 저장합니다."
        >
          <div className="dvvb-form-grid">
            <FormField label="거래처명" required error={errors.name}>
              <TextInput value={name} onChange={setName} error={!!errors.name} />
            </FormField>
            <FormField label="전화번호 / 대표 연락처">
              <TextInput type="tel" value={phone} onChange={setPhone} />
            </FormField>
            <FormField label="주소 / 발주처 메모" wide>
              <TextInput value={address} onChange={setAddress} />
            </FormField>
            <FormField label="담당자">
              <TextInput value={contactName} onChange={setContactName} />
            </FormField>
            <FormField label="SMS 수신 번호" wide>
              <TextInput type="tel" value={smsRecipient} onChange={setSmsRecipient} />
            </FormField>
            <FormField label="리드타임 (일)" error={errors.leadTime}>
              <TextInput type="number" value={leadTime} onChange={setLeadTime} error={!!errors.leadTime} />
            </FormField>
          </div>
        </FormSection>

        <FormSection
          title="플랫폼 기본 정보"
          hint="오프라인은 그대로 두고, 온라인 입력만 별도 탭으로 분리합니다."
        >
          <div className="dvvb-order-tabs" style={{ marginBottom: 14 }}>
            {([
              { value: 'offline', label: '오프라인' },
              { value: 'online', label: '온라인' },
            ] as const).map((option) => (
              <button
                key={option.value}
                type="button"
                className={`dvvb-order-tab${channelType === option.value ? ' is-active' : ''}`}
                onClick={() => setChannelType(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {channelType === 'offline' && (
            <div className="dvvb-channel-card">
              <h4 className="dvvb-h3">오프라인 거래처 등록 폼</h4>
              <div className="dvvb-form-grid" style={{ marginTop: 12 }}>
                <FormField label="오프라인 발주수단">
                  <Select value={channel} onChange={setChannel} options={OFFLINE_CHANNEL_OPTIONS} />
                </FormField>
                <FormField label="수신 담당자 / 채널명">
                  <TextInput value={channelAccount} onChange={setChannelAccount} />
                </FormField>
                <FormField label="전화번호 / 카카오 채널 / 이메일" wide>
                  <TextInput value={channelTarget} onChange={setChannelTarget} />
                </FormField>
                <FormField label="발주 메시지 템플릿" wide>
                  <TextInput value={orderMethod} onChange={setOrderMethod} />
                </FormField>
                <FormField label="발송 연동 방식">
                  <TextInput value={connectorMode} onChange={setConnectorMode} />
                </FormField>
              </div>
              <div className="dvvb-note">
                오프라인은 문자, 카카오톡, 전화, 이메일 수신처와 메시지 양식을 입력합니다.
              </div>
            </div>
          )}

          {channelType === 'online' && (
            <div className="dvvb-channel-card">
              <h4 className="dvvb-h3">온라인 거래처 등록 폼</h4>
              <div className="dvvb-form-grid" style={{ marginTop: 12 }}>
                <FormField label="온라인 플랫폼">
                  <Select value={channel} onChange={setChannel} options={ONLINE_CHANNEL_OPTIONS} />
                </FormField>
                <FormField label="플랫폼 계정 / 발주처명">
                  <TextInput value={channelAccount} onChange={setChannelAccount} />
                </FormField>
                <FormField label="플랫폼 / 몰 주소" wide>
                  <TextInput value={channelTarget} onChange={setChannelTarget} />
                </FormField>
                <FormField label="온라인 발주 방식" wide>
                  <TextInput value={orderMethod} onChange={setOrderMethod} />
                </FormField>
                <FormField label="온라인 연동 방식">
                  <TextInput value={connectorMode} onChange={setConnectorMode} />
                </FormField>
                <FormField label="결제 처리">
                  <Select value={paymentMode} onChange={setPaymentMode} options={PAYMENT_MODE_OPTIONS} />
                </FormField>
                <FormField label="실행 대상">
                  <Select
                    value={executionTarget}
                    onChange={setExecutionTarget}
                    options={EXECUTION_TARGET_OPTIONS}
                  />
                </FormField>
                <FormField label="모바일 열림">
                  <Select
                    value={mobileOpenBehavior}
                    onChange={setMobileOpenBehavior}
                    options={MOBILE_OPEN_BEHAVIOR_OPTIONS}
                  />
                </FormField>
                <FormField label="자동화 가능 범위">
                  <Select
                    value={automationScope}
                    onChange={setAutomationScope}
                    options={AUTOMATION_SCOPE_OPTIONS}
                  />
                </FormField>
                <FormField label="인증 발생 시 처리">
                  <Select
                    value={authChallengeHandling}
                    onChange={setAuthChallengeHandling}
                    options={AUTH_CHALLENGE_OPTIONS}
                  />
                </FormField>
                <FormField label="제품 검색 가능">
                  <Toggle
                    checked={searchableProducts}
                    onChange={setSearchableProducts}
                    label={searchableProducts ? '가능' : '불가'}
                  />
                </FormField>
                <FormField label="과거 주문내역">
                  <Toggle
                    checked={orderHistoryAvailable}
                    onChange={setOrderHistoryAvailable}
                    label={orderHistoryAvailable ? '가능' : '불가'}
                  />
                </FormField>
                <FormField label="URL 바로 열기">
                  <Toggle
                    checked={directUrlOpen}
                    onChange={setDirectUrlOpen}
                    label={directUrlOpen ? '가능' : '불가'}
                  />
                </FormField>
                <FormField label="옵션 전부 선택">
                  <Toggle
                    checked={optionSelectable}
                    onChange={setOptionSelectable}
                    label={optionSelectable ? '가능' : '불가'}
                  />
                </FormField>
                <FormField label="항상 로그인">
                  <Toggle
                    checked={alwaysLoggedIn}
                    onChange={setAlwaysLoggedIn}
                    label={alwaysLoggedIn ? '가능' : '불가'}
                  />
                </FormField>
                <FormField label="SMS 인증 수신">
                  <Toggle
                    checked={smsVerificationRequired}
                    onChange={setSmsVerificationRequired}
                    label={smsVerificationRequired ? '발생 가능' : '미확인'}
                  />
                </FormField>
                <FormField label="이메일 인증">
                  <Toggle
                    checked={emailVerificationRequired}
                    onChange={setEmailVerificationRequired}
                    label={emailVerificationRequired ? '발생 가능' : '미확인'}
                  />
                </FormField>
                <FormField label="Google 인증">
                  <Toggle
                    checked={googleVerificationRequired}
                    onChange={setGoogleVerificationRequired}
                    label={googleVerificationRequired ? '발생 가능' : '미확인'}
                  />
                </FormField>
                <FormField label="기타 인증">
                  <Toggle
                    checked={otherVerificationRequired}
                    onChange={setOtherVerificationRequired}
                    label={otherVerificationRequired ? '발생 가능' : '미확인'}
                  />
                </FormField>
                <FormField label="결제 비밀번호 필요">
                  <Toggle
                    checked={paymentPasswordRequired}
                    onChange={setPaymentPasswordRequired}
                    label={paymentPasswordRequired ? '필요' : '불필요'}
                  />
                </FormField>
                <FormField label="카드 등록됨">
                  <Toggle
                    checked={cardRegistered}
                    onChange={setCardRegistered}
                    label={cardRegistered ? '등록됨' : '미등록'}
                  />
                </FormField>
              </div>
              <div className="dvvb-note">
                온라인은 개인몰, 네이버, 쿠팡처럼 플랫폼별 주문 조건과 실행 한계를 기록합니다.
                인증번호와 결제 비밀번호는 저장하지 않고, 발생 시 사용자가 처리합니다.
              </div>
            </div>
          )}
        </FormSection>

        <FormSection title="공통 발주 설정">
          <div className="dvvb-form-grid">
            <FormField label="자동발주">
              <Toggle checked={autoOrder} onChange={setAutoOrder} label={autoOrder ? '활성' : '비활성'} />
            </FormField>
            <FormField label="기본 발주 시간">
              <TextInput value={defaultOrderTime} onChange={setDefaultOrderTime} placeholder="22:30" />
            </FormField>
            <FormField label="주문 채널">
              <Select value={orderChannel} onChange={setOrderChannel} options={ORDER_CHANNEL_OPTIONS} />
            </FormField>
            <FormField label="승인 방식">
              <Select value={approvalMode} onChange={setApprovalMode} options={APPROVAL_MODE_OPTIONS} />
            </FormField>
            <FormField label="미응답 처리">
              <Select value={fallbackMode} onChange={setFallbackMode} options={FALLBACK_MODE_OPTIONS} />
            </FormField>
          </div>
        </FormSection>
      </div>
    </Modal>
  );
}

function SupplyItemModal({
  state,
  isOwner,
  submitting,
  onClose,
  onSaved,
}: {
  state:
    | { mode: 'create'; supplier: SupplierRow }
    | { mode: 'edit'; supplier: SupplierRow; index: number; item: SupplyItem };
  isOwner: boolean;
  submitting: boolean;
  onClose: () => void;
  onSaved: (items: SupplyItem[]) => void;
}) {
  const initial = state.mode === 'edit' ? state.item : emptySupplyItem();
  const isCreate = state.mode === 'create';
  const [materialQuery, setMaterialQuery] = useState(initial.sourceName || initial.internalName);
  const [sourceId, setSourceId] = useState(initial.sourceId ?? '');
  const [sourceName, setSourceName] = useState(initial.sourceName || initial.internalName);
  const [sourceUnit, setSourceUnit] = useState(initial.sourceUnit || initial.unit);
  const [sourceLocationId, setSourceLocationId] = useState(initial.sourceLocationId);
  const [sourceLocationName, setSourceLocationName] = useState(initial.sourceLocationName);
  const [providerKind, setProviderKind] = useState<InventoryMaterialRow['providerKind']>(
    initial.providerKind,
  );
  const [providerItemId, setProviderItemId] = useState(initial.providerItemId);
  const [conversionRatio, setConversionRatio] = useState<number | null>(initial.conversionRatio);
  const [supplierName, setSupplierName] = useState(initial.supplierName);
  const [unit, setUnit] = useState(initial.orderUnit || initial.unit);
  const [defaultQty, setDefaultQty] = useState(String(initial.defaultQty));
  const [safetyStock, setSafetyStock] = useState(String(initial.safetyStock));
  const [autoEnabled, setAutoEnabled] = useState<SupplyItem['autoEnabled']>(initial.autoEnabled);
  const [memo, setMemo] = useState(initial.memo);
  const [selectedItems, setSelectedItems] = useState<SupplyItem[]>([]);
  const [errors, setErrors] = useState<{
    sourceId?: string;
  }>({});

  const linkedSourceIds = useMemo(() => {
    const ids = new Set<string>();
    supplyItemsFromMetadata(state.supplier.metadata).forEach((item, index) => {
      if (state.mode === 'edit' && index === state.index) return;
      if (item.sourceId) ids.add(item.sourceId);
    });
    return ids;
  }, [state]);

  const selectedSourceIds = useMemo(() => {
    return new Set(selectedItems.map((item) => item.sourceId).filter((id): id is string => !!id));
  }, [selectedItems]);

  const materialsQ = useQuery({
    queryKey: ['inventory', 'materials', 'supplier-map', materialQuery.trim()],
    queryFn: async () => {
      const r = await inventoryControllerListMaterials({
        query: {
          limit: '100',
          ...(materialQuery.trim() ? { q: materialQuery.trim() } : {}),
        } as never,
      });
      return unwrap<MaterialsEnvelope>(r);
    },
    staleTime: 30_000,
  });

  const materials = materialsQ.data?.items ?? [];

  function selectMaterial(row: InventoryMaterialRow) {
    if (linkedSourceIds.has(row.id) || selectedSourceIds.has(row.id)) return;
    if (isCreate) {
      setSelectedItems((current) => {
        if (current.some((item) => item.sourceId === row.id)) return current;
        return [...current, supplyItemFromMaterial(row)];
      });
      setErrors({});
      return;
    }
    const nextOrderUnit = materialOrderUnit(row);
    setSourceId(row.id);
    setSourceName(row.name);
    setSourceUnit(row.unit || nextOrderUnit);
    setSourceLocationId(row.locationId);
    setSourceLocationName(row.locationName);
    setProviderKind(row.providerKind);
    setProviderItemId(row.providerItemId);
    setConversionRatio(row.conversionRatio);
    setUnit(nextOrderUnit);
    setSafetyStock(String(Math.max(0, Number(row.minStock) || 0)));
    if (!supplierName.trim() || supplierName.trim() === initial.supplierName || supplierName.trim() === initial.internalName) {
      setSupplierName(row.name);
    }
    setMaterialQuery(row.name);
  }

  function submit() {
    const next: typeof errors = {};
    if (isCreate && selectedItems.length === 0) {
      next.sourceId = '추가할 제품을 하나 이상 선택하세요.';
    }
    if (!isCreate && (!sourceId || !sourceName.trim())) {
      next.sourceId = '제품 이름을 검색해서 선택하세요.';
    }
    if (sourceId && linkedSourceIds.has(sourceId)) next.sourceId = '이미 이 거래처에 연결된 재료입니다.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    if (isCreate) {
      onSaved(
        selectedItems.map((item) => ({
          ...item,
          autoEnabled,
          memo: memo.trim(),
        })),
      );
      return;
    }
    const finalSupplierName = supplierName.trim() || sourceName.trim();
    onSaved([{
      internalName: sourceName.trim(),
      supplierName: finalSupplierName,
      unit: unit.trim() || '개',
      defaultQty: Math.max(0.0001, Number(defaultQty) || 1),
      safetyStock: Math.max(0, Number(safetyStock) || 0),
      autoEnabled,
      memo: memo.trim(),
      sourceKind: 'inventory',
      sourceId,
      sourceName: sourceName.trim(),
      sourceUnit: sourceUnit.trim() || unit.trim() || '개',
      sourceLocationId,
      sourceLocationName,
      providerKind,
      providerItemId,
      orderUnit: unit.trim() || sourceUnit.trim() || '개',
      conversionRatio,
    }]);
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={state.mode === 'edit' ? '제품 매핑 수정' : '제품 이름으로 추가'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            취소
          </Button>
          <Button
            variant="strong"
            onClick={submit}
            disabled={submitting || !isOwner}
            title={!isOwner ? '권한 없음 (owner 전용)' : ''}
          >
            {submitting
              ? '저장 중…'
              : isCreate
                ? `선택한 제품 저장 (${selectedItems.length})`
                : '저장'}
          </Button>
        </>
      }
    >
      <div className="dvvb-form-grid">
        <FormField
          label="제품 이름 검색"
          required
          error={errors.sourceId}
          hint="여기서는 제품과 거래처만 연결합니다. 발주단위와 수량은 자동발주에서 처리합니다."
          wide
        >
          <TextInput
            type="search"
            value={materialQuery}
            onChange={setMaterialQuery}
            placeholder="제품 이름 검색"
            error={!!errors.sourceId}
          />
        </FormField>

        {isCreate && (
          <FormField label={`추가할 제품 (${selectedItems.length})`} wide>
            {selectedItems.length === 0 ? (
              <div className="dvvb-note" style={{ marginTop: 0 }}>
                검색 결과에서 제품을 여러 개 담은 뒤 한 번에 저장하세요.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {selectedItems.map((item) => (
                  <div key={item.sourceId ?? item.internalName} className="dvvb-mapping-item">
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: 'var(--fg-1)', fontSize: 13, fontWeight: 800 }}>
                        {item.sourceName || item.internalName}
                      </div>
                      <div className="dvvb-copy" style={{ marginTop: 3 }}>
                        {item.sourceLocationName ?? '사업장 미확인'}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setSelectedItems((current) =>
                          current.filter((candidate) => candidate.sourceId !== item.sourceId),
                        )
                      }
                    >
                      제거
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </FormField>
        )}

        <div style={{ gridColumn: '1 / -1', display: 'grid', gap: 8 }}>
          {materialsQ.isLoading ? (
            <div className="dvvb-note" style={{ marginTop: 0 }}>
              재료를 불러오는 중입니다.
            </div>
          ) : materials.length === 0 ? (
            <div className="dvvb-note" style={{ marginTop: 0 }}>
              검색 결과가 없습니다. 먼저 재고 &gt; 재료에서 우리 재료를 등록해야 합니다.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 6, maxHeight: 220, overflow: 'auto' }}>
              {materials.slice(0, 20).map((row) => {
                const selected = row.id === sourceId;
                const alreadyLinked = linkedSourceIds.has(row.id);
                const alreadySelected = selectedSourceIds.has(row.id);
                return (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => selectMaterial(row)}
                    disabled={alreadyLinked || alreadySelected}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      gap: 10,
                      alignItems: 'center',
                      padding: '10px 12px',
                      border: `1px solid ${selected ? 'var(--primary)' : 'var(--border-base)'}`,
                      borderRadius: 'var(--radius-sm)',
                      background: selected ? 'var(--primary-bg)' : 'var(--bg-surface)',
                      cursor: alreadyLinked || alreadySelected ? 'not-allowed' : 'pointer',
                      opacity: alreadyLinked || alreadySelected ? 0.55 : 1,
                    }}
                  >
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', color: 'var(--fg-1)', fontSize: 13, fontWeight: 800 }}>
                        {row.name}
                      </span>
                      <span className="dvvb-copy" style={{ display: 'block', marginTop: 3 }}>
                        {row.locationName ?? '사업장 미확인'} · 현재 {formatAmount(row.stock)} {row.unit}
                      </span>
                    </span>
                    <span className={`dvvb-pill${selected ? ' is-good' : ''}`}>
                      {alreadyLinked ? '이미 연결됨' : alreadySelected ? '담김' : selected ? '선택됨' : '담기'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {!isCreate && (
          <>
            <FormField label="선택된 제품" wide>
              <div className="dvvb-note" style={{ marginTop: 0 }}>
                {sourceId
                  ? `${sourceName} · ${sourceLocationName ?? '사업장 미확인'}`
                  : '검색 결과에서 제품을 선택하세요.'}
              </div>
            </FormField>
            <FormField
              label="거래처 표시명"
              hint="거래처에 보낼 이름이 다를 때만 수정합니다."
              wide
            >
              <TextInput value={supplierName} onChange={setSupplierName} placeholder={sourceName || '선택한 제품명 사용'} />
            </FormField>
          </>
        )}
        <FormField label="자동발주 사용">
          <Select
            value={autoEnabled}
            onChange={(value) => setAutoEnabled(value === '미사용' ? '미사용' : '사용')}
            options={AUTO_ENABLED_OPTIONS}
          />
        </FormField>
        <FormField label="발주 메모" wide>
          <Textarea value={memo} onChange={setMemo} />
        </FormField>
      </div>
    </Modal>
  );
}

function FormSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <section className="dvvb-form-section">
      <h3 className="dvvb-h3">{title}</h3>
      {hint && <div className="dvvb-copy" style={{ marginTop: 4, marginBottom: 12 }}>{hint}</div>}
      <div style={{ display: 'grid', gap: 12, marginTop: hint ? 0 : 12 }}>{children}</div>
    </section>
  );
}

function SectionHead({
  title,
  sub,
  action,
}: {
  title: string;
  sub?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="dvvb-section-head">
      <div style={{ minWidth: 0 }}>
        <h2 className="dvvb-title">{title}</h2>
        {sub && (
          <div className="dvvb-copy" style={{ marginTop: 4, maxWidth: 760 }}>
            {sub}
          </div>
        )}
      </div>
      {action}
    </div>
  );
}
